import {
  faCaretDown,
  faPlusCircle,
  faRefresh,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FuzzySearcher, trackEvent, uuidV4 } from '@readcloud/common';
import { getSubdomainAssets } from '@readcloud/common/lib/utils/utils';
import {
  Button,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@readcloud/ui';
import type { Cloud, User } from '@readcloud/data';
import React, { forwardRef } from 'react';
import { ListGroupItem } from 'react-bootstrap';
import { useDebounce } from 'use-debounce';
import { CloudWithBooks, CreateCloudOperation } from '../types';
import { CreateCloudFormModal } from './CloudFormModal';
import { ReactComponent as Logo } from './rc_logo.svg';
import { useCloudsAndContentActions } from '../context/ActionsContext';
import { ReactComponent as CloudIcon } from './Cloud.svg';
import {
  homeSettingsSelectors,
  homeSettingsActions,
  jobsActions,
  useAppDispatch,
} from '@root/libs/state/src';
import { useSelector } from 'react-redux';
import { Authorized } from './session/AuthorizedComponent';

/**
 * The dropdown button used, traditionally, in the top toolbar of the ReadCloud clouds view
 * for adding clouds, navigating to them in the scrollable area, or refreshing.
 */
const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

export const CloudsDropdown = forwardRef<
  HTMLDivElement,
  {
    /** The full list of clouds to show in the dropdown.*/
    clouds: CloudWithBooks[];
    /** Whether the app is refreshing or not */
    refreshing: boolean;
    /** Triggered when the user wants to refresh the app; the user clicks on the refresh button */
    onRefresh: () => void;
    /** When a cloud is clicked/selected from the dropdown */
    onCloudSelected: (cloudId: string) => void;
    knownUsers: User[];
    currentUserId: string;
    onCreateCloud: CreateCloudOperation;
    experimentalDesign?: boolean;
  }
>(function CloudsDropdown(props, ref) {
  const { refreshing, onCloudSelected, clouds, onRefresh } = props;
  const [cloudsSearchText, setCloudsSearchText] = React.useState('');
  const [showCloudsPopover, setShowCloudsPopover] = React.useState(false);

  const [debouncedCloudsSearchText, __] = useDebounce(cloudsSearchText, 100);
  const actions = useCloudsAndContentActions();
  const homeSettings = useSelector(homeSettingsSelectors.getSettings);
  const dispatch = useAppDispatch();

  const cloudSearcher = React.useMemo(
    () =>
      new FuzzySearcher(clouds, {
        keySelector: (cloud) => cloud.name,
      }),
    [clouds]
  );
  const cloudsArrayRankedBySearch = React.useMemo(
    () =>
      debouncedCloudsSearchText
        ? cloudSearcher.search(debouncedCloudsSearchText)
        : clouds,
    [clouds, debouncedCloudsSearchText]
  );

  const [isCloudAdderOpen, setIsCloudAdderOpen] = React.useState(false);

  const {name} = getSubdomainAssets();

  const nameMap = {
    'My Purchases': name.MY_PURCHASES,
    'My Interactive Logins': name.MY_INTERACTIVE_LOGINS,
    'My Content': name.MY_CONTENTS
  };

  return (
    <>
      <Popover
        open={showCloudsPopover}
        onOpenChange={(show) => {
          setShowCloudsPopover(show);
          if (!show) setCloudsSearchText('');
        }}
      >
        <PopoverTrigger asChild>
          <Button variant="link" className="rc-gap-2 rc-text-brand-primary">
            <div className="md:rc-block rc-hidden">
              <CloudIcon style={{ width: '2em', height: 'auto' }} />
            </div>
            <span className="rc-whitespace-nowrap">My Clouds</span>
            <FontAwesomeIcon size="lg" icon={faCaretDown} />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="rc-relative rc-flex rc-flex-col rc-w-auto rc-max-w-[100vw] rc-rounded rc-p-3 rc-py-4 rc-gap-2 rc-z-[52]"
          style={{
            maxHeight: 'min(25rem, 70vh)', transform: "translateY(20px)"
           }}
        >
          {!isTauri && (
            <Authorized
              requiredPermissions={[['content']]}
              requiredAccess={{ read: true }}
            >
              <Button
                variant="ghost"
                onClick={() => {
                  trackEvent('Webapp/LaunchContentManagerClicked');
                  actions.gotoContent();
                }}
              >
                Launch Content Manager
              </Button>
            </Authorized>
          )}
          <div className="rc-flex rc-gap-2">
            <Button
              size="sm"
              disabled={refreshing}
              onClick={() => {
                onRefresh();
                setShowCloudsPopover(false);
              }}
              variant="ghost"
            >
              <FontAwesomeIcon size="lg" icon={faRefresh} />
            </Button>

            <Input
              className="rc-shadow-none"
              name="cloud-search"
              onChange={(e) => setCloudsSearchText(e.target.value)}
              value={cloudsSearchText}
              placeholder="Search"
            />
            <Authorized
              requiredPermissions={[['content']]}
              requiredAccess={{ read: true }}
              hiddenComponent={<div className="rc-pr-1"></div>}
            >
              <Button
                size="sm"
                disabled={isCloudAdderOpen}
                onClick={() => {
                  trackEvent('Webapp/CloudAddClick');
                  setIsCloudAdderOpen(true);
                }}
                variant="ghost"
              >
                <FontAwesomeIcon size="lg" icon={faPlusCircle} />
              </Button>
            </Authorized>
          </div>
          <div className="rc-flex rc-flex-col rc-overflow-auto">
            {cloudsArrayRankedBySearch.map((match, i) => (
              <Button
                data-testid={`cloudSearchResult-${i}`}
                variant="ghost"
                className="rc-justify-start rc-text-start rc-rounded-none rc-h-auto rc-px-3"
                onClick={() => {
                  onCloudSelected(match.id);
                  setShowCloudsPopover(false);
                }}
                key={match.id}
              >
                {nameMap[match.name] || match.name}
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <CreateCloudFormModal
        onCreateCloud={(cloud) => {
          return props.onCreateCloud(cloud).then((res) => {
            if (homeSettings.hideEmptyClouds) {
              dispatch(
                homeSettingsActions.updateHomeSettings({
                  hideEmptyClouds: false,
                })
              );
              dispatch(
                jobsActions.addJob({
                  id: uuidV4(),
                  name: 'Empty Clouds are now visible',
                  description:
                    "Hide them again via 'Settings > Hide Empty Clouds'",
                })
              );
            }
            setIsCloudAdderOpen(false);
            return res;
          });
        }}
        currentUserId={props.currentUserId}
        knownUsers={props.knownUsers}
        onCancel={() => setIsCloudAdderOpen(false)}
        isOpen={isCloudAdderOpen}
        canDeleteCloud={false}
      />
    </>
  );
});
